/**
 * useCropProfilePhoto — Hook wrapping the cropProfilePhoto and
 * cropDirectorProfilePhoto GraphQL mutations with loading/error state.
 *
 * On failure, displays a toast notification and keeps the modal open so
 * the user can retry. Returns the new `croppedPhotoS3Key` on success.
 *
 * Feature: avatar-crop
 * Requirements: 5.1, 6.5
 */

import { useCallback, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { toast } from 'sonner';
import { cropProfilePhoto, cropDirectorProfilePhoto } from '@/graphql/mutations';
import type { CropParams } from '@/lib/crop-constraints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CropProfilePhotoInput {
  originalS3Key: string;
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
}

export interface CropDirectorProfilePhotoInput extends CropProfilePhotoInput {
  targetUserId: string;
}

export interface CropProfilePhotoResult {
  croppedPhotoS3Key: string;
}

export interface UseCropProfilePhotoOptions {
  /** Injectable Amplify client for tests. */
  client?: {
    graphql: (args: {
      query: string;
      variables?: Record<string, unknown>;
    }) => Promise<unknown>;
  };
}

export interface UseCropProfilePhotoReturn {
  /** Execute the cropProfilePhoto mutation for the current user. */
  cropPhoto: (
    originalS3Key: string,
    params: CropParams,
  ) => Promise<CropProfilePhotoResult | null>;
  /** Execute the cropDirectorProfilePhoto mutation for a target user. */
  cropDirectorPhoto: (
    targetUserId: string,
    originalS3Key: string,
    params: CropParams,
  ) => Promise<CropProfilePhotoResult | null>;
  /** Whether a crop mutation is currently in flight. */
  loading: boolean;
  /** The most recent error, or null. */
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Module-level client
// ---------------------------------------------------------------------------

const defaultClient = generateClient() as unknown as {
  graphql: (args: {
    query: string;
    variables?: Record<string, unknown>;
  }) => Promise<unknown>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCropProfilePhoto(
  options?: UseCropProfilePhotoOptions,
): UseCropProfilePhotoReturn {
  const client = options?.client ?? defaultClient;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const cropPhoto = useCallback(
    async (
      originalS3Key: string,
      params: CropParams,
    ): Promise<CropProfilePhotoResult | null> => {
      setLoading(true);
      setError(null);

      try {
        const input: CropProfilePhotoInput = {
          originalS3Key,
          cropX: params.x,
          cropY: params.y,
          cropWidth: params.width,
          cropHeight: params.height,
        };

        const result = (await client.graphql({
          query: cropProfilePhoto,
          variables: { input },
        })) as GraphQLResult<{
          cropProfilePhoto: CropProfilePhotoResult;
        }>;

        return result.data?.cropProfilePhoto ?? null;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save crop. Please try again.';
        const error = err instanceof Error ? err : new Error(message);
        setError(error);
        toast.error('Failed to save crop. Please try again.');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  const cropDirectorPhoto = useCallback(
    async (
      targetUserId: string,
      originalS3Key: string,
      params: CropParams,
    ): Promise<CropProfilePhotoResult | null> => {
      setLoading(true);
      setError(null);

      try {
        const input: CropDirectorProfilePhotoInput = {
          targetUserId,
          originalS3Key,
          cropX: params.x,
          cropY: params.y,
          cropWidth: params.width,
          cropHeight: params.height,
        };

        const result = (await client.graphql({
          query: cropDirectorProfilePhoto,
          variables: { input },
        })) as GraphQLResult<{
          cropDirectorProfilePhoto: CropProfilePhotoResult;
        }>;

        return result.data?.cropDirectorProfilePhoto ?? null;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save crop. Please try again.';
        const error = err instanceof Error ? err : new Error(message);
        setError(error);
        toast.error('Failed to save crop. Please try again.');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  return { cropPhoto, cropDirectorPhoto, loading, error };
}
